import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../assets/icons/LineDot";
import {
  ChartTitle,
  StyledSubtitle,
  StyledTile,
} from "../../../../../../components/ChartCard/ChartCard.styles";
import { ChartCardLoading } from "../../../../../../components/ChartCard/ChartCardLoading";
import { ChartSpecificValues } from "../../../../../../components/ChartCard/ChartSpecificValues/ChartSpecificValues";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { ChartLegendContainer } from "../../../../../../components/ModalChart/CustomModalChartLegend/CustomModalChartLegend.style";
import { useFitbitActivity } from "../../../../../../hooks/queries/fitbit";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { formatHoursAndMinutes } from "../../../../../../utils/dateFormatter";
import { FitbitActivityChart } from "../../../MeasurementModal/components/FitbitActivityChart/FitbitActivityChart";
import { GraphContainer, GraphLegend } from "./FitbitChartCard.styles";

export type FitbitChartCardProps = {
  patientId?: string;
  title: string;
  onClick: React.MouseEventHandler<HTMLDivElement>;
};

export function FitbitChartCard({
  patientId,
  title,
  onClick,
}: FitbitChartCardProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter } = useUserPreferences();
  const dateRangeFilter = getDateRangeFilter();

  const { activityData, isActivityFetching, isActivityRefetching } =
    useFitbitActivity(
      parseInt(patientId ?? "0", 10),
      dateRangeFilter.startDate.startOf("day"),
      dateRangeFilter.endDate.endOf("day")
    );

  const specificValues = useMemo(() => {
    if (!activityData?.statistics) {
      return [
        { label: t("patientPage_card_meanActive"), value: "-" },
        { label: t("patientPage_card_meanSedentary"), value: "-" },
      ];
    }

    const { statistics } = activityData;

    return [
      {
        label: t("patientPage_card_meanActive"),
        value: formatHoursAndMinutes(statistics.meanActiveHoursPerDay),
      },
      {
        label: t("patientPage_card_meanSedentary"),
        value: formatHoursAndMinutes(statistics.meanSedentaryHoursPerDay),
      },
    ];
  }, [activityData?.statistics, t]);

  const chartData = useMemo(() => {
    if (!activityData) return [];

    if (activityData.viewMode === "monthly") {
      return (activityData.monthlyReadings ?? []).map((reading) => {
        // Center monthly bars at the middle of the month (15th day, noon)
        // Parse the date and set to 15th day at noon
        const dateParts = reading.monthStart.split("T")[0].split("-");
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
        const monthDate = new Date(year, month, 15, 12, 0, 0, 0);
        return {
          timeValue: monthDate.getTime(),
          sedentaryHours: reading.avgSedentaryHours,
          activeHours: reading.avgActiveHours,
          label: reading.monthLabel,
          isMonthly: true,
          sedentaryPercent: reading.sedentaryPercent,
          lightlyActivePercent: reading.lightlyActivePercent,
          fairlyActivePercent: reading.fairlyActivePercent,
          veryActivePercent: reading.veryActivePercent,
          minutesSedentary: reading.avgMinutesSedentary,
          minutesLightlyActive: reading.avgMinutesLightlyActive,
          minutesFairlyActive: reading.avgMinutesFairlyActive,
          minutesVeryActive: reading.avgMinutesVeryActive,
        };
      });
    }

    return (activityData.dailyReadings ?? []).map((reading) => {
      // Center daily bars at noon to align better with X-axis ticks
      // Parse the date string and set time to noon, preserving the calendar day
      const dateParts = reading.date.split("T")[0].split("-");
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
      const day = parseInt(dateParts[2], 10);
      const date = new Date(year, month, day, 12, 0, 0, 0);
      return {
        timeValue: date.getTime(),
        sedentaryHours: reading.sedentaryHours,
        activeHours: reading.activeHours,
        isMonthly: false,
        sedentaryPercent: reading.sedentaryPercent,
        lightlyActivePercent: reading.lightlyActivePercent,
        fairlyActivePercent: reading.fairlyActivePercent,
        veryActivePercent: reading.veryActivePercent,
        minutesSedentary: reading.minutesSedentary,
        minutesLightlyActive: reading.minutesLightlyActive,
        minutesFairlyActive: reading.minutesFairlyActive,
        minutesVeryActive: reading.minutesVeryActive,
      };
    });
  }, [activityData]);

  const isEmptyState =
    !activityData ||
    chartData.length === 0 ||
    chartData.every(
      (d) => (d.activeHours ?? 0) === 0 && (d.sedentaryHours ?? 0) === 0
    );

  return (
    <StyledTile onClick={onClick}>
      {isActivityFetching || isActivityRefetching ? (
        <ChartCardLoading />
      ) : (
        <>
          <ChartTitle>{title}</ChartTitle>
          {isEmptyState ? (
            <StyledSubtitle>{t("patientPage_card_noData")}</StyledSubtitle>
          ) : null}

          <GraphContainer isEmpty={isEmptyState}>
            {!isEmptyState ? (
              <GraphLegend>
                <ChartLegendContainer>
                  <StyledLegend key="Active">
                    <LineDot fill="#C87C36" />
                    {t("patientPage_measurementModal_activity_legendActive")}
                  </StyledLegend>
                  <StyledLegend key="Sedentary">
                    <LineDot fill="#54A6AA" />
                    {t("patientPage_measurementModal_activity_legendSedentary")}
                  </StyledLegend>
                </ChartLegendContainer>
              </GraphLegend>
            ) : null}
            <FitbitActivityChart
              data={chartData}
              dateInterval={dateRangeFilter}
              height="90%"
              width="100%"
              onlyGraph
            />
          </GraphContainer>

          <ChartSpecificValues values={specificValues} isEmpty={isEmptyState} />
        </>
      )}
    </StyledTile>
  );
}
